<!--
  - Copyright (c) 2019 brightlamp
  -->

<template>
    <div>
        <v-dialog v-model="dialogLocal" @keydown.esc="closeDialog" scrollable max-width="1100px">
            <v-card>
                <v-alert
                        v-model="saveDefaultsAlert"
                        dismissible
                        type="success">
                    User defaults updated.
                </v-alert>
                <v-alert
                        v-model="missingDataAlert"
                        dismissible
                        type="warning">
                    For graphs, data and parameters need to be selected. For tables, only data needs to be selected.
                </v-alert>
                <v-card-title>
                    <span class="headline" align="center" style="color:#FFFD00;background-color:#363636;width:100%;display:block;">{{formTitle}}</span>
                </v-card-title>
                <v-card-actions>
                    <v-spacer/>
                    <v-btn class="pr-5" color="blue darken-1" text @click="closeDialog">Close</v-btn>
                </v-card-actions>
                <v-card-text>
                    <v-progress-linear v-if="waiting" :indeterminate="true"/>
                    <v-container>
                        <v-row>
                            <v-col xs="4" sm="4" md="3">
                                <v-menu
                                        v-model="startDateMenu"
                                        :close-on-content-click="false"
                                        :nudge-right="40"
                                        transition="scale-transition"
                                        offset-y
                                        max-width="290px"
                                        min-width="290px">
                                    <template v-slot:activator="{ on }">
                                        <v-text-field
                                                v-model="startDateFormatted"
                                                label="Filter By Start Date"
                                                prepend-icon="mdi-calendar"
                                                readonly
                                                v-on="on">
                                        </v-text-field>
                                    </template>
                                    <v-date-picker color="blue" v-model="startDate" no-title :events="testEvents" event-color="yellow accent-4" @input="startDateMenu = false"/>
                                </v-menu>
                            </v-col>
                            <v-col xs="4" sm="4" md="3">
                                <v-menu
                                        ref="endDateMenu"
                                        v-model="endDateMenu"
                                        :close-on-content-click="false"
                                        :nudge-right="40"
                                        transition="scale-transition"
                                        offset-y
                                        max-width="290px"
                                        min-width="290px">
                                    <template v-slot:activator="{ on }">
                                        <v-text-field
                                                v-model="endDateFormatted"
                                                label="Filter By End Date"
                                                prepend-icon="mdi-calendar"
                                                readonly
                                                v-on="on">
                                        </v-text-field>
                                    </template>
                                    <v-date-picker color="blue" v-model="endDate" no-title :events="testEvents" event-color="yellow accent-4" @input="endDateMenu = false"/>
                                </v-menu>
                            </v-col>
                        </v-row>
                    </v-container>
                    <v-container>
                        <v-row>
                            <v-col xs=12 md=6>
                                <v-card tile flat class="blue lighten-1 pa-1" :height="508">
                                    <v-card tile flat :height="500">
                                        <v-card tile flat>
                                            <v-data-table
                                                    v-model="selected"
                                                    :headers="headers"
                                                    :items="filteredTests"
                                                    disable-pagination
                                                    item-key="visitId"
                                                    show-select
                                                    hide-default-footer
                                                    style="max-height: 500px; overflow-y: auto">
                                                <template v-slot:item.baseline="{ item }">
                                                    <v-chip label :style="{color: 'black', backgroundColor: (item.baseline === 'Yes' ? '#E65100' : 'transparent')}">{{ item.baseline }}</v-chip>
                                                </template>
                                            </v-data-table>
                                        </v-card>
                                    </v-card>
                                </v-card>
                            </v-col>
                            <v-col xs="12" md="6">
                                <v-select
                                        v-model="resultType"
                                        :items="resultTypesFiltered"
                                        label="Filter By Test Type"
                                        @change="resultTypeChanged">
                                </v-select>
                                <v-select
                                        v-model="eye"
                                        :items="eyes"
                                        label="Filter By Eye"
                                        @change="eyeChanged">
                                </v-select>
                                <v-checkbox v-if="trendingOutput !== 'trending-output-t'"
                                        v-model="parameterNamesCheckBox"
                                        label="Select/Deselect All Parameters"
                                        :indeterminate="parameterNamesCheckBoxIndeterminateState"
                                        @change="checkAllAction"
                                        color="black"
                                        hide-details>
                                </v-checkbox>
                                <v-select v-if="trendingOutput !== 'trending-output-t'"
                                        v-model="parameters"
                                        :items="parameterNamesFiltered"
                                        label="Select Parameters"
                                        multiple
                                        chips>
                                </v-select>
                                <v-card flat>
                                    <v-card-actions>
                                        <v-btn color="accent" @click="loadDefaults(false)">
                                            <v-icon style="color: #000000;" left>mdi-file-download</v-icon>
                                            <span style="color: #000000;">Load Defaults</span>
                                        </v-btn>
                                        <v-spacer/>
                                        <v-btn color="accent" @click="setAsDefault">
                                            <v-icon style="color: #000000;" left>mdi-content-save</v-icon>
                                            <span style="color: #000000;">Save Defaults</span>
                                        </v-btn>
                                    </v-card-actions>
                                    <b>Note: Load defaults will unselect data.</b>
                                </v-card>
                            </v-col>
                        </v-row>
                        <v-card flat>
                            <v-card-actions>
                                <v-radio-group v-model="trendingOutput" hint="Table only choice is parameter agnostic" persistent-hint row>
                                    <v-radio label="View Graphs & Tables" value="trending-output-gt" color="black"/>
                                    <v-radio label="View Graphs" value="trending-output-g" color="black"/>
                                    <v-radio label="View Tables" value="trending-output-t" color="black"/>
                                </v-radio-group>
                                <v-spacer/>
                                <v-btn color="accent" @click="processQuery">
                                    <v-icon style="color: #000000;" left>mdi-file-send</v-icon>
                                    <span style="color: #000000;">Goto Results</span>
                                </v-btn>
                            </v-card-actions>
                        </v-card>
                    </v-container>
                </v-card-text>
            </v-card>
        </v-dialog>
        <v-dialog
                v-model="graphDialog"
                fullscreen
                hide-overlay
                transition="dialog-bottom-transition"
                scrollable>
            <v-card tile>
                <div>
                    <v-alert
                            v-model="saveGraphsAlert"
                            dismissible
                            type="success">
                        Saving of graphs complete.
                    </v-alert>
                    <v-alert
                        v-model="nothingToSaveAlert"
                        dismissible
                        type="warning">
                        There is nothing to save.
                    </v-alert>
                    <v-toolbar dense class="blue white--text">
                        <v-btn icon dark @click="graphDialog = false; nothingToSaveAlert = false">
                            <v-icon>mdi-close</v-icon>
                        </v-btn>
                        <v-toolbar-title>{{ formTitle }}</v-toolbar-title>
                        <v-spacer/>
                        <v-toolbar-items>
                            <v-tooltip bottom color="black" open-delay="500">
                                <template #activator="{ on }">
                                    <v-btn v-on="on" icon dark :loading="saving" :disabled="saving" @click="save">Save
                                        <template v-slot:loader>
                                            <span class="custom-loader">
                                                <v-icon light>mdi-sync</v-icon>
                                            </span>
                                        </template>
                                    </v-btn>
                                </template>
                                <span>Save graphs to a PDF file</span>
                            </v-tooltip>
                        </v-toolbar-items>
                    </v-toolbar>
                    <v-progress-linear :active="saving" bottom striped :value="saveProgress" color="deep-purple accent-4"/>
                </div>
                <v-card-text id="trendingDataId">
                    <v-container fluid>
                        <v-row dense>
                            <template v-if="trendingOutput === 'trending-output-gt' || trendingOutput === 'trending-output-g'">
                                <template v-for="item in testsAndParameters">
                                    <template v-if="eye === 'Right' || (eye === 'Both' && item.rightItems.length > 0)">
                                        <v-col cols="12" lg="6">
                                            <TrendingGraph :eyeLocation="'Right'" :testsAndParameters="item" :saving="saving" :elementId="`graphId${!item.showTogether && item.leftItems.length > 0 ? item.graphIndex - 1 : item.graphIndex}`"/>
                                        </v-col>
                                    </template>
                                    <template v-if="eye === 'Left' || (eye === 'Both' && !item.showTogether && item.leftItems.length > 0)">
                                        <v-col cols="12" lg="6">
                                            <TrendingGraph :eyeLocation="'Left'" :testsAndParameters="item" :saving="saving" :elementId="`graphId${item.graphIndex}`"/>
                                        </v-col>
                                    </template>
                                </template>
                                <template v-for="(item) in pupillogramTests">
                                    <v-col cols="12">
                                        <TrendingPupillogramGraph :pupillogramTests="item" :saving="saving" :elementId="`graphId${item.graphIndex}`"/>
                                    </v-col>
                                </template>
                            </template>
                            <template v-if="trendingOutput === 'trending-output-gt' || trendingOutput === 'trending-output-t'">
                                    <template v-for="(item, index) in tableDataTests">
                                    <v-col cols="12">
                                        <TrendingDataTable :showExpanded="trendingOutput === 'trending-output-t'" :resultType="resultType" :tableData="tableDataTests[index]" :elementId="`graphId${tableDataTests[index].graphIndex}`"/>
                                    </v-col>
                                </template>
                            </template>
                        </v-row>
                    </v-container>
                </v-card-text>
            </v-card>
        </v-dialog>
     </div>
</template>

<script>
    import { mapState, mapActions, mapMutations } from 'vuex'
    import TrendingGraph from '@/components/trending-graph'
    import TrendingPupillogramGraph from '@/components/trending-pupillogram-graph'
    import TrendingDataTable from '@/components/trending-data-table'
    import { resultTypes, parameterNames, eyes, formatNumber, returnAge, returnTorchLevelIndex } from '@/store/tests'
    import html2canvas from 'html2canvas'
    import * as jsPDF from 'jspdf'
    import { v4 as uuidv4 } from 'uuid'

    export default {
        name: 'trending-dialog',
        components:
        {
            TrendingGraph,
            TrendingPupillogramGraph,
            TrendingDataTable
        },
        data: function()
        {
            return {
                saveDefaultsAlert: false,
                missingDataAlert: false,
                nothingToSaveAlert: false,
                dialogLocal: false,
                graphDialog: false,
                startDateMenu: false,
                startDate: null,
                startDateFormatted: null,
                startDateAsDate: null,
                endDateMenu: false,
                endDate: null,
                endDateFormatted: null,
                endDateAsDate: null,
                resultTypesFiltered: [],
                eye: 'Both',
                resultType: null,
                parameters: [],
                convertedTests: [],
                selected: [],
                headers:
                [
                    {
                        text: 'Test Date',
                        value: 'visitDate',
                        sortable: false
                    },
                    {
                        text: 'Eye Location',
                        value: 'eyeLocation',
                        sortable: false
                    },
                    {
                        text: 'Baseline',
                        value: 'baseline',
                        sortable: false
                    }
                ],
                graphIndex: 0,
                filteredTests: [],
                testsAndParameters: [],
                pupillogramTests: [],
                tableDataTests: [],
                saving: false,
                saveProgress: 0.0,
                saveGraphsAlert: false,
                parameterNamesCheckBox: false,
                parameterNamesCheckBoxIndeterminateState: false,
                trendingOutput: 'trending-output-gt'
            }
        },
        props:
        {
            dialog: Boolean(false)
        },
        computed:
        {
            ...mapState('auth',
            {
                currentUser: state => state.currentUser ? state.currentUser : {},
                currentUserChanges: state => state.currentUserChanges ? state.currentUserChanges : {}
            }),
            ...mapState('tests',
            {
                tests: state => state.tests,
                normalizedData: state => state.normalizedData
            }),
            ...mapState('common',
            {
                waiting: state => state.waiting
            }),
            formTitle()
            {
                return `Trending Data for Patient ID: ${this.tests.length > 0 ? this.tests[0].mrn : ''}`
            },
            parameterNamesFiltered()
            {
                switch (this.resultType)
                {
                    case resultTypes[0]:
                        return [
                            parameterNames[12],
                            parameterNames[10],
                            parameterNames[0],
                            parameterNames[1],
                            parameterNames[3],
                            parameterNames[4],
                            parameterNames[5],
                            parameterNames[6],
                            parameterNames[7],
                            parameterNames[13],
                            parameterNames[8],
                            parameterNames[14],
                            parameterNames[11]
                        ];
                    case resultTypes[1]:
                        return [
                            parameterNames[12],
                            parameterNames[10],
                            parameterNames[0],
                            parameterNames[1],
                            parameterNames[2],
                            parameterNames[3],
                            parameterNames[4],
                            parameterNames[5],
                            parameterNames[6],
                            parameterNames[7],
                            parameterNames[13],
                            parameterNames[8],
                            parameterNames[14],
                            parameterNames[11],
                            parameterNames[9]
                        ];
                    case resultTypes[2]:
                        return [
                            parameterNames[12],
                            parameterNames[10],
                            parameterNames[0],
                            parameterNames[1],
                            parameterNames[2],
                            parameterNames[3],
                            parameterNames[4],
                            parameterNames[5],
                            parameterNames[6],
                            parameterNames[7],
                            parameterNames[13],
                            parameterNames[8],
                            parameterNames[14],
                            parameterNames[11],
                            parameterNames[9]
                        ];
                    case resultTypes[3]:
                        return [
                            parameterNames[12],
                            parameterNames[10],
                            parameterNames[1],
                            parameterNames[6],
                            parameterNames[7],
                            parameterNames[13],
                            parameterNames[8],
                            parameterNames[14],
                            parameterNames[11]
                        ];
                    case resultTypes[4]:
                        return [
                            parameterNames[12],
                            parameterNames[10],
                            parameterNames[0],
                            parameterNames[1],
                            parameterNames[2],
                            parameterNames[3],
                            parameterNames[4],
                            parameterNames[5],
                            parameterNames[6],
                            parameterNames[7],
                            parameterNames[13],
                            parameterNames[8],
                            parameterNames[9],
                            parameterNames[14],
                            parameterNames[11]
                        ];
                    case resultTypes[5]:
                        return [
                            parameterNames[12],
                            parameterNames[10],
                            parameterNames[0],
                            parameterNames[1],
                            parameterNames[2],
                            parameterNames[3],
                            parameterNames[4],
                            parameterNames[5],
                            parameterNames[6],
                            parameterNames[7],
                            parameterNames[13],
                            parameterNames[8],
                            parameterNames[9],
                            parameterNames[14],
                            parameterNames[11]
                        ];
                    default:
                        return []
                }
            },
            testEvents()
            {
                let events = [];
                // Set the start date and end dates for the calendars if we have tests
                if (this.convertedTests.length > 0)
                {
                    this.endDate = this.formatDateForCalendar(this.convertedTests[0].visitDateAsDate);
                    this.startDate = this.formatDateForCalendar(this.convertedTests[this.convertedTests.length - 1].visitDateAsDate)
                }
                // Set all of the tests as events in the calendars
                for (const test of this.convertedTests)
                {
                    events.push(this.formatDateForCalendar(test.visitDateAsDate))
                }
                return events
            },
            eyes()
            {
                return eyes
            }
        },
        watch:
        {
            dialogLocal(val)
            {
                // If the dialog is being closed
                if (!val)
                {
                    this.resultTypesFiltered = [];
                    this.filteredTests = [];
                    this.selected = [];
                    this.testsAndParameters = [];
                    this.pupillogramTests = [];
                    this.tableDataTests = [];
                    this.$emit('dialog-close')
                }
                else // Else the dialog is being open
                {
                    this.$logEvent(this.$firebaseAnalytics, 'screen_view',
                    {
                        app_name: 'Web Portal',
                        screen_name: `Reflex Web Portal - ${this.$options.name}`,
                        app_version: process.env.VUE_APP_VERSION
                    });
                    this.dialogLocal = val;
                    this.convertTests();
                    this.setResultTypes();
                    this.filterTests()
                }
            },
            dialog(val)
            {
                this.dialogLocal = val
            },
            graphDialog(val)
            {
                // If the dialog is being closed
                if (!val)
                {
                    document.getElementById('trendingDataId').scrollTop = 0
                }
            },
            startDateFormatted()
            {
                this.filterTests()
            },
            endDateFormatted()
            {
                this.filterTests()
            },
            startDate(val)
            {
                this.startDateAsDate = new Date(val)
                this.startDateFormatted = this.formatDate(val)
            },
            endDate(val)
            {
                this.endDateAsDate = new Date(val)
                this.endDateFormatted = this.formatDate(val)
            },
            parameters()
            {
                this.parameterNamesCheckBoxIndeterminateState = this.parameters.length > 0 && this.parameters.length < this.parameterNamesFiltered.length;
                this.parameterNamesCheckBox = this.parameters.length === this.parameterNamesFiltered.length
            },
            resultType()
            {
                this.resultTypeChanged()
            }
        },
        mounted()
        {
            this.loadDefaults(true)
        },
        methods:
        {
            ...mapActions('tests', ['GET_NORMALIZED_DATA']),
            ...mapActions('auth', ['UPDATE_CURRENT_USER']),
            ...mapMutations('auth', ['SET_CURRENT_USER']),
            ...mapMutations('common', ['WAITING', 'SHOW_SNACKBAR_ERROR', 'HIDE_SNACKBAR']),
            closeDialog()
            {
                this.$emit('dialog-close')
            },
            loadDefaults(hideAlert)
            {
                if (this.currentUserChanges.preferences.dataTrendPreferences)
                {
                    this.resultType = this.currentUserChanges.preferences.dataTrendPreferences.resultType;
                    this.parameters = this.currentUserChanges.preferences.dataTrendPreferences.parameters;
                    this.eye = this.currentUserChanges.preferences.dataTrendPreferences.eye;
                    this.trendingOutput = this.currentUserChanges.preferences.dataTrendPreferences.trendingOutput;
                    this.filterTests()
                }
                else if (!hideAlert)
                {
                    this.SHOW_SNACKBAR_ERROR('You do not have any defaults yet to load.')
                }
            },
            async setAsDefault()
            {
                try
                {
                    this.currentUserChanges.preferences.dataTrendPreferences =
                    {
                        resultType: this.resultType,
                        parameters: this.parameters,
                        eye: this.eye,
                        trendingOutput: this.trendingOutput
                    };
                    this.HIDE_SNACKBAR();
                    this.WAITING(true);
                    await this.UPDATE_CURRENT_USER(this.currentUserChanges);
                    this.saveDefaultsAlert = true;
                    setTimeout(() =>
                    {
                        this.saveDefaultsAlert = false;
                    }, 2000)
                }
                catch (error)
                {
                    this.SHOW_SNACKBAR_ERROR(error.message)
                }
                this.WAITING(false)
            },
            eyeChanged()
            {
                this.filterTests()
            },
            resultTypeChanged()
            {
                this.parameters = [];
                this.filterTests()
            },
            checkAllAction()
            {
                this.parameters = this.parameterNamesCheckBox ? this.parameterNamesFiltered : []
            },
            async processQuery()
            {
                // Sort tests in ascending order by test date
                this.selected.sort((a, b) =>
                {
                    return a.visitDateAsDate < b.visitDateAsDate ? -1 : 1
                });
                const mostRecentTest = this.selected[this.selected.length - 1];

                await this.loadNormalizedData();

                this.testsAndParameters = [];
                let normalizedData = {};
                this.graphIndex = 0;

                if (this.trendingOutput !== 'trending-output-t' && mostRecentTest)
                {
                    // Alpha Omega Pupil
                    if (this.parameters.includes(parameterNames[12]))
                    {
                        this.processQueryCommon('alphaOmega', 12, '', null, null)
                    }
                    // Amplitude
                    if (this.parameters.includes(parameterNames[10]))
                    {
                        normalizedData = this.getMinMaxNormalizedValues(10, mostRecentTest);
                        this.processQueryCommon('amp', 10, '(mm)', normalizedData.minNormalData, normalizedData.maxNormalData)
                    }
                    // Average Constriction Speed
                    if (this.parameters.includes(parameterNames[0]))
                    {
                        normalizedData = this.getMinMaxNormalizedValues(0, mostRecentTest);
                        this.processQueryCommon('avgConSpd', 0, '(mm/sec)', normalizedData.minNormalData, normalizedData.maxNormalData)
                    }
                    // Average Diameter
                    if (this.parameters.includes(parameterNames[1]))
                    {
                        normalizedData = this.getMinMaxNormalizedValues(1, mostRecentTest);
                        this.processQueryCommon('avgDiamtr', 1, '(mm)', normalizedData.minNormalData, normalizedData.maxNormalData)
                    }
                    // Average Dilation Velocity
                    if (this.parameters.includes(parameterNames[2]))
                    {
                        normalizedData = this.getMinMaxNormalizedValues(2, mostRecentTest);
                        this.processQueryCommon('dilationVel', 2, '(mm/sec)', normalizedData.minNormalData, normalizedData.maxNormalData)
                    }
                    // Constriction Time
                    if (this.parameters.includes(parameterNames[3]))
                    {
                        normalizedData = this.getMinMaxNormalizedValues(3, mostRecentTest);
                        this.processQueryCommon('timeToMnDiamtr', 3, '(sec)', normalizedData.minNormalData, normalizedData.maxNormalData)
                    }
                    // Latency
                    if (this.parameters.includes(parameterNames[4]))
                    {
                        normalizedData = this.getMinMaxNormalizedValues(4, mostRecentTest);
                        this.processQueryCommon('latncy', 4, '(sec)', normalizedData.minNormalData, normalizedData.maxNormalData)
                    }
                    // Maximum Constriction Speed
                    if (this.parameters.includes(parameterNames[5]))
                    {
                        normalizedData = this.getMinMaxNormalizedValues(5, mostRecentTest);
                        this.processQueryCommon('mxConSpd', 5, '(mm/sec)', normalizedData.minNormalData, normalizedData.maxNormalData)
                    }
                    // Maximum Diameter
                    if (this.parameters.includes(parameterNames[6]))
                    {
                        normalizedData = this.getMinMaxNormalizedValues(6, mostRecentTest);
                        this.processQueryCommon('mxDiamtr', 6, '(mm)', normalizedData.minNormalData, normalizedData.maxNormalData)
                    }
                    // Minimum Diameter
                    if (this.parameters.includes(parameterNames[7]))
                    {
                        normalizedData = this.getMinMaxNormalizedValues(7, mostRecentTest);
                        this.processQueryCommon('mnDiamtr', 7, '(mm)', normalizedData.minNormalData, normalizedData.maxNormalData)
                    }
                    // Neuro CDS Score
                    if (this.parameters.includes(parameterNames[13]))
                    {
                        this.processQueryCommon('concussCdsScore', 13, '', null, null)
                    }
                    // Reflex Score
                    if (this.parameters.includes(parameterNames[14]))
                    {
                        this.processQueryCommon('reflexScore', 14, '', null, null)
                    }
                    // Release Amplitude
                    if (this.parameters.includes(parameterNames[11]))
                    {
                        normalizedData = this.getMinMaxNormalizedValues(11, mostRecentTest);
                        this.processQueryCommon('ramp', 11, '(mm)', normalizedData.minNormalData, normalizedData.maxNormalData)
                    }
                    // 75% Recovery Time
                    if (this.parameters.includes(parameterNames[9]))
                    {
                        normalizedData = this.getMinMaxNormalizedValues(9, mostRecentTest);
                        this.processQueryCommon('recTime75', 9, '(sec)', normalizedData.minNormalData, normalizedData.maxNormalData)
                    }
                }

                this.pupillogramTests = [];
                this.tableDataTests = [];
                const pupillogramRequested = this.parameters.includes(parameterNames[8]);
                // Pupillogram and Data Tables
                if (pupillogramRequested || this.trendingOutput !== 'trending-output-g')
                {
                    let baseline = {right: null, left: null};
                    let baselineCount = {right: 0, left: 0};
                    let maxPupillogramLength = 0;
                    let oneBaselineFound = {right: false, left: false};
                    let hasBaseline = false;

                    // Pull out the single baseline for each eye if there is just one for each
                    for (const test of this.selected)
                    {
                        if (test.rightEye)
                        {
                            if ('Yes' === test.baseline)
                            {
                                if (!baseline.right)
                                {
                                    baseline.right = test
                                }
                                baselineCount.right++
                            }
                        }
                        else
                        {
                            if ('Yes' === test.baseline)
                            {
                                if (!baseline.left)
                                {
                                    baseline.left = test
                                }
                                baselineCount.left++
                            }
                        }
                        if (pupillogramRequested)
                        {
                            maxPupillogramLength = maxPupillogramLength < test.timeSeries.length ? test.timeSeries.length : maxPupillogramLength;
                        }
                    }
                    oneBaselineFound.right = 1 === baselineCount.right;
                    oneBaselineFound.left = 1 === baselineCount.left;

                    let index = 0;
                    let items = [];
                    const testsPerGraph = 8;
                    const colors = ['#b71c1c', '#880e4f', '#4a148c', '#311b92', '#1a237e', '#01579b', '#006064', '#004d40'];
                    let colorIndex = 0;
                    if (pupillogramRequested && this.trendingOutput !== 'trending-output-t')
                    {
                        for (const test of this.selected)
                        {
                            // Skip this test if it was captured as a baseline
                            if ('Yes' === test.baseline && ((test.rightEye && oneBaselineFound.right) || (!test.rightEye && oneBaselineFound.left)))
                            {
                                hasBaseline = true;
                                continue
                            }
                            index++;
                            colorIndex = (index - 1) % testsPerGraph;
                            items.push({
                                index: index,
                                rightEye: test.rightEye,
                                item: test.timeSeries,
                                color: colors[colorIndex]
                            });
                            // Make a new graph for every 8 tests
                            if (index % testsPerGraph === 0)
                            {
                                this.pupillogramTests.push(
                                {
                                    graphIndex: this.graphIndex++,
                                    rightBaseline: oneBaselineFound.right ? baseline.right.timeSeries : null,
                                    leftBaseline: oneBaselineFound.left ? baseline.left.timeSeries : null,
                                    items: items,
                                    maxPupillogramLength: maxPupillogramLength
                                });
                                items = []
                            }
                        }
                        // If there is any left over, make a new graph or if no items were selected other than baselines
                        if (index % testsPerGraph !== 0 || (0 === index && hasBaseline))
                        {
                            this.pupillogramTests.push(
                            {
                                graphIndex: this.graphIndex++,
                                rightBaseline: oneBaselineFound.right ? baseline.right.timeSeries : null,
                                leftBaseline: oneBaselineFound.left ? baseline.left.timeSeries : null,
                                items: items,
                                maxPupillogramLength: maxPupillogramLength
                            })
                        }
                    }

                    if (this.trendingOutput !== 'trending-output-g')
                    {
                        let testsForTable = [];
                        let allNormalizedData = [];
                        index = 0;

                        // Get all the normalized values
                        for (let parameterIndex = 0; parameterIndex < parameterNames.length; ++parameterIndex)
                        {
                            // Skip Pupillogram, Alpha Omega Pupil, Neuro CDS Score, or Reflex Score
                            if (8 === parameterIndex || 12 === parameterIndex || 13 === parameterIndex || 14 === parameterIndex)
                            {
                                continue
                            }
                            allNormalizedData.push(this.getMinMaxNormalizedValues(parameterIndex, mostRecentTest))
                        }

                        for (const test of this.selected)
                        {
                            // Skip this test if it was captured as a baseline
                            if ('Yes' === test.baseline && ((test.rightEye && oneBaselineFound.right) || (!test.rightEye && oneBaselineFound.left)))
                            {
                                hasBaseline = true;
                                continue
                            }
                            index++;
                            test.index = index;
                            testsForTable.push(test);
                            // Make a new table for every 8 tests
                            if (index % testsPerGraph === 0)
                            {
                                this.tableDataTests.push(
                                {
                                    graphIndex: this.graphIndex++,
                                    items: testsForTable,
                                    allNormalizedData: allNormalizedData,
                                    rightBaseline: oneBaselineFound.right ? baseline.right : null,
                                    leftBaseline: oneBaselineFound.left ? baseline.left : null
                                });
                                testsForTable = []
                            }
                        }
                        // If there is any left over, make a new table or if no items were selected other than baselines
                        if (index % testsPerGraph !== 0 || (0 === index && hasBaseline))
                        {
                            this.tableDataTests.push(
                            {
                                graphIndex: this.graphIndex++,
                                items: testsForTable,
                                allNormalizedData: allNormalizedData,
                                rightBaseline: oneBaselineFound.right ? baseline.right : null,
                                leftBaseline: oneBaselineFound.left ? baseline.left : null
                            })
                        }
                    }
                }
                // Warn the user if missing data or parameters
                if (this.selected.length === 0 ||
                   (this.testsAndParameters.length === 0 && this.pupillogramTests.length === 0 && this.trendingOutput !== 'trending-output-t') ||
                   (this.tableDataTests.length === 0 && this.trendingOutput !== 'trending-output-g'))
                {
                    this.missingDataAlert = true;
                    return
                }
                this.missingDataAlert = false;
                this.graphDialog = true
            },
            processQueryCommon(parameter, parameterIndex, units, minNormalData, maxNormalData)
            {
                let index = {right: 1, left: 1};
                let items = {right: [], left: []};
                let baseline = {right: null, left: null};
                let baselineCount = {right: 0, left: 0};
                let oneBaselineFound = {right: false, left: false};

                // Pull out the single baseline for each eye if there is just one for each
                for (const test of this.selected)
                {
                    if (test.rightEye)
                    {
                        if ('Yes' === test.baseline)
                        {
                            if (!baseline.right)
                            {
                                baseline.right = {item: test[parameter] ? test[parameter] : 'N/A', testDate: test.visitDate}
                            }
                            baselineCount.right++
                        }
                    }
                    else
                    {
                        if ('Yes' === test.baseline)
                        {
                            if (!baseline.left)
                            {
                                baseline.left = {item: test[parameter] ? test[parameter] : 'N/A', testDate: test.visitDate}
                            }
                            baselineCount.left++
                        }
                    }
                }
                oneBaselineFound.right = 1 === baselineCount.right;
                oneBaselineFound.left = 1 === baselineCount.left;

                for (const test of this.selected)
                {
                    // Skip this test if it was captured as a baseline
                    if ('Yes' === test.baseline && ((test.rightEye && oneBaselineFound.right) || (!test.rightEye && oneBaselineFound.left)))
                    {
                        continue
                    }

                    if (test.rightEye)
                    {
                        items.right.push({index: index.right, item: test[parameter] ? test[parameter] : 'N/A', testDate: test.visitDate});
                        index.right++
                    }
                    else
                    {
                        items.left.push({index: index.left, item: test[parameter] ? test[parameter] : 'N/A', testDate: test.visitDate});
                        index.left++
                    }
                }
                this.testsAndParameters.push(
                {
                    graphIndex: index.right > 1 && index.left > 1 && index.right !== index.left ? this.graphIndex += 1 : this.graphIndex++,
                    showTogether: index.right === index.left,
                    rightBaseline: oneBaselineFound.right ? baseline.right : null,
                    leftBaseline: oneBaselineFound.left ? baseline.left : null,
                    parameterName: parameterNames[parameterIndex],
                    parameterLabel: `${parameterNames[parameterIndex]} ${units}`,
                    rightItems: items.right,
                    leftItems: items.left,
                    minNormalData: minNormalData,
                    maxNormalData: maxNormalData,
                    meanData: minNormalData !== null && maxNormalData !== null ? (maxNormalData - minNormalData) / 2 + minNormalData : null
                });
                if (index.right > 1 && index.left > 1 && index.right !== index.left)
                {
                    this.graphIndex++
                }
            },
            formatDate(date)
            {
                if (!date)
                {
                    return null
                }
                // Account for date with . as separators
                if (date.split('T')[0].includes('.'))
                {
                    return date
                }
                const theDate = new Date(date);
                return theDate.toLocaleDateString(navigator.languages[0], {timeZone: 'UTC'})
            },
            formatDateForCalendar(date)
            {
                return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`
            },
            filterTests()
            {
                this.selected = [];

                // Filter any tests that are in error
                this.filteredTests = this.convertedTests.filter((value) =>
                {
                    return 0 === value.errCode
                });

                // If we have a valid start date
                if ((this.startDateAsDate instanceof Date && !isNaN(this.startDateAsDate)) || (this.endDateAsDate instanceof Date && !isNaN(this.endDateAsDate)))
                {
                    // Filter based on date range
                    this.filteredTests = this.filteredTests.filter((value) =>
                    {
                        const valueDateOnly = new Date(new Date(value.visitDateAsDate).toISOString().split('T')[0])
                        if (this.startDateAsDate instanceof Date && !isNaN(this.startDateAsDate) && this.endDateAsDate instanceof Date && !isNaN(this.endDateAsDate))
                        {
                            return valueDateOnly >= this.startDateAsDate && valueDateOnly <= this.endDateAsDate
                        }
                        if (this.startDateAsDate instanceof Date && !isNaN(this.startDateAsDate))
                        {
                            return valueDateOnly >= this.startDateAsDate
                        }
                        if (this.endDateAsDate instanceof Date && !isNaN(this.endDateAsDate))
                        {
                            return valueDateOnly <= this.endDateAsDate
                        }
                        return true
                    });
                }

                // Filter based on eye location
                this.filteredTests = this.filteredTests.filter((value) =>
                {
                    if ('Both' === this.eye)
                    {
                        return true
                    }
                    return this.eye === value.eyeLocation
                });

                // Filter based on test type
                this.filteredTests = this.filteredTests.filter((value) =>
                {
                    return this.resultType === value.resultType
                });

                // Sort tests with baselines first in descending order by test date
                this.filteredTests.sort((a, b) =>
                {
                    let comparison = 0;
                    if (a.baseline === 'Yes' && b.baseline === 'No')
                    {
                        comparison = -1
                    }
                    else if  ((a.baseline === 'Yes' && b.baseline === 'Yes') || (a.baseline === 'No' && b.baseline === 'No'))
                    {
                        return b.visitDateAsDate - a.visitDateAsDate
                    }
                    else
                    {
                        return comparison = 1
                    }
                    return comparison
                });

                let rightEyeFound = false;
                let leftEyeFound = false;
                // Automatically select the latest right and left eye baselines
                for (const test of this.filteredTests)
                {
                    if (!rightEyeFound && test.rightEye && test.baseline === 'Yes')
                    {
                        rightEyeFound = true;
                        this.selected.push(test)
                    }
                    else if (!leftEyeFound && !test.rightEye && test.baseline === 'Yes')
                    {
                        leftEyeFound = true;
                        this.selected.push(test)
                    }
                }
            },
            setResultTypes()
            {
                let constrictCount = 0;
                let fullSpectrumFlashCount = 0;
                let fullSpectrumTorchCount = 0;
                let noLightCount = 0;
                let fullSpectrumFlashAmbientCount = 0;
                let fullSpectrumTorchAmbientCount = 0;

                // Count how many tests of each type there are
                for (const test of this.convertedTests)
                {
                    if (resultTypes[0] === test.resultType)
                    {
                        constrictCount++
                    }
                    else if (resultTypes[1] === test.resultType)
                    {
                        fullSpectrumFlashCount++
                    }
                    else if (resultTypes[2] === test.resultType)
                    {
                        fullSpectrumTorchCount++
                    }
                    else if (resultTypes[3] === test.resultType)
                    {
                        noLightCount++
                    }
                    else if (resultTypes[4] === test.resultType)
                    {
                        fullSpectrumFlashAmbientCount++
                    }
                    else if (resultTypes[5] === test.resultType)
                    {
                        fullSpectrumTorchAmbientCount++
                    }
                }
                // If there are just one test type in the test list, pick it as the default selection
                if (constrictCount > 0 && 0 === fullSpectrumFlashCount && 0 === fullSpectrumTorchCount && 0 === noLightCount && 0 === fullSpectrumFlashAmbientCount && 0 === fullSpectrumTorchAmbientCount)
                {
                    this.resultType = resultTypes[0];
                    this.resultTypesFiltered.push(resultTypes[0]);
                    return
                }
                else if (fullSpectrumFlashCount > 0 && 0 === constrictCount && 0 === fullSpectrumTorchCount && 0 === noLightCount && 0 === fullSpectrumFlashAmbientCount && 0 === fullSpectrumTorchAmbientCount)
                {
                    this.resultType = resultTypes[1];
                    this.resultTypesFiltered.push(resultTypes[1]);
                    return
                }
                else if (fullSpectrumTorchCount > 0 && 0 === constrictCount && 0 === fullSpectrumFlashCount && 0 === noLightCount && 0 === fullSpectrumFlashAmbientCount && 0 === fullSpectrumTorchAmbientCount)
                {
                    this.resultType = resultTypes[2];
                    this.resultTypesFiltered.push(resultTypes[2]);
                    return
                }
                else if (noLightCount > 0 && 0 === constrictCount && 0 === fullSpectrumFlashCount && 0 === fullSpectrumTorchCount && 0 === fullSpectrumFlashAmbientCount && 0 === fullSpectrumTorchAmbientCount)
                {
                    this.resultType = resultTypes[3];
                    this.resultTypesFiltered.push(resultTypes[3]);
                    return
                }
                else if (fullSpectrumFlashAmbientCount > 0 && 0 === constrictCount && 0 === fullSpectrumFlashCount && 0 === fullSpectrumTorchCount && 0 === noLightCount && 0 === fullSpectrumTorchAmbientCount)
                {
                    this.resultType = resultTypes[4];
                    this.resultTypesFiltered.push(resultTypes[4]);
                    return
                }
                else if (fullSpectrumTorchAmbientCount > 0 && 0 === constrictCount && 0 === fullSpectrumFlashCount && 0 === fullSpectrumTorchCount && 0 === noLightCount && 0 === fullSpectrumFlashAmbientCount)
                {
                    this.resultType = resultTypes[5];
                    this.resultTypesFiltered.push(resultTypes[5]);
                    return
                }
                // Add the tests to the list
                if (constrictCount > 0)
                {
                    this.resultTypesFiltered.push(resultTypes[0])
                }
                if (fullSpectrumFlashCount > 0)
                {
                    this.resultTypesFiltered.push(resultTypes[1])
                }
                if (fullSpectrumTorchCount > 0)
                {
                    this.resultTypesFiltered.push(resultTypes[2])
                }
                if (noLightCount > 0)
                {
                    this.resultTypesFiltered.push(resultTypes[3])
                }
                if (fullSpectrumFlashAmbientCount > 0)
                {
                    this.resultTypesFiltered.push(resultTypes[4])
                }
                if (fullSpectrumTorchAmbientCount > 0)
                {
                    this.resultTypesFiltered.push(resultTypes[5])
                }
                // Use the previously selected test
                if (this.resultTypesFiltered.includes(this.resultType))
                {
                    return
                }
                // There are multiple test types in the test list, default to one starting with constrict
                if (fullSpectrumTorchAmbientCount > 0)
                {
                    this.resultType = resultTypes[5]
                }
                if (fullSpectrumFlashAmbientCount > 0)
                {
                    this.resultType = resultTypes[4]
                }
                if (noLightCount > 0)
                {
                    this.resultType = resultTypes[3]
                }
                if (fullSpectrumTorchCount > 0)
                {
                    this.resultType = resultTypes[2]
                }
                if (fullSpectrumFlashCount > 0)
                {
                    this.resultType = resultTypes[1]
                }
                if (constrictCount > 0)
                {
                    this.resultType = resultTypes[0]
                }
            },
            async loadNormalizedData()
            {
                try
                {
                    this.HIDE_SNACKBAR();
                    this.WAITING(true);
                    await this.GET_NORMALIZED_DATA()
                }
                catch (error)
                {
                    this.SHOW_SNACKBAR_ERROR(error.message)
                }
                this.WAITING(false)
            },
            getMinMaxNormalizedValues(parameterIndex, patientVisit)
            {
                if (null == patientVisit)
                {
                    return null
                }
                let resultTypeIndex = 0;
                switch (this.resultType)
                {
                    case resultTypes[0]:
                        resultTypeIndex = 0;
                        break;
                    case resultTypes[1]:
                        resultTypeIndex = 1;
                        break;
                    case resultTypes[2]:
                        resultTypeIndex = 2;
                        break;
                    case resultTypes[3]:
                        resultTypeIndex = 3;
                        break;
                    case resultTypes[4]:
                        resultTypeIndex = 4;
                        break;
                    default:
                        resultTypeIndex = 5;
                        break
                }
                const torchLevelIndex = returnTorchLevelIndex(resultTypeIndex, patientVisit.metaData.torchLevel);
                const normalizedDataMinMax = this.normalizedData.data[`parameter${parameterIndex}`][resultTypeIndex][returnAge(this.tests[0].dob, patientVisit.visitDate)][torchLevelIndex];
                return ({
                    minNormalData: formatNumber(normalizedDataMinMax.minNormalData),
                    maxNormalData: formatNumber(normalizedDataMinMax.maxNormalData)
                })
            },
            async save()
            {
                // Abort of there are no graphs to process
                if (this.testsAndParameters.length === 0 && this.pupillogramTests.length === 0 && this.tableDataTests.length === 0)
                {
                    return
                }
                this.saving = true;
                let pdf = null;
                await this.$nextTick(async function ()
                {
                    for (let index = 0; index < this.graphIndex; ++index)
                    {
                        this.saveProgress = (index + 1) / this.graphIndex * 100;

                        const canvas = document.getElementById(`graphId${index}`);
                        if (!canvas)
                        {
                            this.nothingToSaveAlert = true;
                            this.saving = false;
                            return
                        }

                        const width = canvas.getBoundingClientRect().width + 45;
                        const height = canvas.getBoundingClientRect().height + 39;
                        const image = await html2canvas(document.getElementById(`graphId${index}`), {
                            scale: 1,
                            width: width,
                            height: height
                        });
                        const canvas_img = image.toDataURL("image/png", 1.0);

                        if (index === 0)
                        {
                            pdf = new jsPDF('l', 'pt', [width, height])
                        }
                        else if (index > 0 && index < this.graphIndex)
                        {
                            pdf.addPage([width, height])
                        }
                        await pdf.addImage(canvas_img, 'png', .5, 0)
                    }
                    pdf.save(`${this.tests[0].mrn}-${uuidv4()}.pdf`);
                    this.saveProgress = 100;
                    this.saving = false;
                    this.saveGraphsAlert = true;
                    setTimeout(() =>
                    {
                        this.saveGraphsAlert = false
                    }, 2500)
                })
            },
            convertTests() // Convert tests from paired eye tests to individual tests to work with
            {
                this.convertedTests = [];
                for (const test of this.tests)
                {
                    for (const eye of test.eyes)
                    {
                        const convertedTest = {};
                        convertedTest.mrn = test.mrn;
                        convertedTest.visitDateAsDate = test.visitDateAsDate;
                        convertedTest.visitDate = test.visitDate;
                        convertedTest.baseline = eye.baseline;
                        convertedTest.visitId = eye.visitId;
                        convertedTest.rightEye = eye.rightEye;
                        convertedTest.eyeLocation = eye.eyeLocation;
                        convertedTest.resultType = eye.resultType;
                        convertedTest.amp = eye.amp;
                        convertedTest.avgConSpd = eye.avgConSpd;
                        convertedTest.avgDiamtr = eye.avgDiamtr;
                        convertedTest.dilationVel = eye.dilationVel;
                        convertedTest.timeToMnDiamtr = eye.timeToMnDiamtr;
                        convertedTest.latncy = eye.latncy;
                        convertedTest.mxConSpd = eye.mxConSpd;
                        convertedTest.mxDiamtr = eye.mxDiamtr;
                        convertedTest.mnDiamtr = eye.mnDiamtr;
                        convertedTest.ramp = eye.ramp;
                        convertedTest.recTime75 = eye.recTime75;
                        convertedTest.timeSeries = eye.timeSeries;
                        convertedTest.alphaOmega = eye.alphaOmega;
                        convertedTest.reflexScore = eye.rawReflexScore;
                        convertedTest.concussCdsScore = eye.concussCdsScore !== null && eye.concussCdsScore !== undefined ? eye.concussCdsScore.split(' ')[0] : 0
                        convertedTest.errCode = eye.errCode;
                        convertedTest.metaData = eye.metaData;
                        this.convertedTests.push(convertedTest)
                    }
                }
            }
        }
    }
</script>

<style scoped>
    .custom-loader {
        animation: loader 1s infinite;
        display: flex;
    }
    @-moz-keyframes loader {
        from {
            transform: rotate(0);
        }
        to {
            transform: rotate(360deg);
        }
    }
    @-webkit-keyframes loader {
        from {
            transform: rotate(0);
        }
        to {
            transform: rotate(360deg);
        }
    }
    @-o-keyframes loader {
        from {
            transform: rotate(0);
        }
        to {
            transform: rotate(360deg);
        }
    }
    @keyframes loader {
        from {
            transform: rotate(0);
        }
        to {
            transform: rotate(360deg);
        }
    }
</style>